import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Image } from '../components/apartment/header/header.component';
import { Navbar } from '../types/navbar.types';
import { Category } from '../components/hof/hof-page/categories/categories.component';
import type { Product } from '@TupiC/zehentmoar-components';
import { BlogPost } from '../components/hof/blog/blog-post/blog-post.component';
import { Footer } from '../types/footer.types';
import { BlockNode } from '../types/rich-text.types';
import { pageBySlugQuery } from '../queries/page.queries';
import { shopByPageSlugQuery } from '../queries/shop.queries';
import { GraphQLPage, Page } from './renderer.service';
import { hofPageQuery } from '../queries/hof-page.queries';
import { GraphQLHofPage } from '../components/hof/hof-page/hof-page.component';

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    private http: HttpClient = inject(HttpClient);

    getPageBySlug(slug: string, languageCode: string): Observable<Page> {
        return this.http.get<Page>(
            `${environment.apiUrl}/api/pages?filters[slug][$eq]=${slug}&locale=${languageCode}&pLevel=5`,
        );
    }

    getPageBySlugGraphql(
        slug: string,
        languageCode: string,
    ): Observable<GraphQLPage> {
        return this.retryRequest<GraphQLPage>(
            (headers) =>
                this.http.post<GraphQLPage>(
                    `${environment.apiUrl}/graphql`,
                    { query: pageBySlugQuery(slug, languageCode) },
                    {
                        headers: headers.set(
                            'Content-Type',
                            'application/json',
                        ),
                    },
                ),
            3,
            250,
        );
    }

    getHofPageBySlugGraphql(
        slug: string,
        languageCode: string,
    ): Observable<GraphQLHofPage> {
        return this.retryRequest<GraphQLHofPage>(
            (headers) =>
                this.http.post<GraphQLHofPage>(
                    `${environment.apiUrl}/graphql`,
                    { query: hofPageQuery(slug, languageCode) },
                    {
                        headers: headers.set(
                            'Content-Type',
                            'application/json',
                        ),
                    },
                ),
            3,
            250,
        );
    }

    getShopDataByPageSlugGraphql(
        slug: string,
        languageCode: string,
    ): Observable<GraphQLShopData> {
        return this.retryRequest<GraphQLShopData>(
            (headers) =>
                this.http.post<GraphQLShopData>(
                    `${environment.apiUrl}/graphql`,
                    { query: shopByPageSlugQuery(slug, languageCode) },
                    {
                        headers: headers.set(
                            'Content-Type',
                            'application/json',
                        ),
                    },
                ),
            3,
            250,
        );
    }

    getProductBySlug(slug: string): Observable<ProductResponse> {
        return this.http.get<ProductResponse>(
            `${environment.apiUrl}/api/products?filters[slug][$eq]=${slug}&pLevel=4`,
        );
    }

    getProductByDocumentId(
        documentId: string,
    ): Observable<SingleProductResponse> {
        return this.http.get<SingleProductResponse>(
            `${environment.apiUrl}/api/products/${documentId}?pLevel=4`,
        );
    }

    getOrderBySession(sessionId: string): Observable<OrderConfirmation> {
        return this.http.get<OrderConfirmation>(
            `${environment.apiUrl}/api/checkout/order-by-session?session_id=${encodeURIComponent(sessionId)}`,
        );
    }

    getProducts(): Observable<ProductResponse> {
        return this.http.get<ProductResponse>(
            `${environment.apiUrl}/api/products?pLevel=4`,
        );
    }

    getApartmentNavbar(languageCode: string): Observable<Navbar> {
        const url = `${environment.apiUrl}/api/nav-bar?locale=${languageCode}&pLevel=4`;
        return this.retryRequest<Navbar>(
            (headers) => this.http.get<Navbar>(url, { headers }),
            3,
            250,
        );
    }

    getHofNavbar(languageCode: string): Observable<Navbar> {
        const url = `${environment.apiUrl}/api/navbar-hof?locale=${languageCode}&pLevel=4`;
        return this.retryRequest<Navbar>(
            (headers) => this.http.get<Navbar>(url, { headers }),
            3,
            250,
        );
    }

    getApartmentFooter(languageCode: string): Observable<Footer> {
        const url = `${environment.apiUrl}/api/footer?locale=${languageCode}&pLevel=4`;
        return this.retryRequest<Footer>(
            (headers) => this.http.get<Footer>(url, { headers }),
            3,
            250,
        );
    }

    getHofFooter(languageCode: string): Observable<Footer> {
        const url = `${environment.apiUrl}/api/footer-hof?locale=${languageCode}&pLevel=4`;
        return this.retryRequest<Footer>(
            (headers) => this.http.get<Footer>(url, { headers }),
            3,
            250,
        );
    }

    createNewsletterEntry(data: NewsletterPayload): Observable<any> {
        return this.http.post(`${environment.apiUrl}/api/newsletters`, data);
    }

    getBlogPosts(): Observable<BlogPostResponse> {
        return this.http.get<BlogPostResponse>(
            `${environment.apiUrl}/api/blog-posts?sort=date:desc&pLevel=4`,
        );
    }

    getBlogPostBySlug(slug: string): Observable<BlogPostResponse> {
        return this.http.get<BlogPostResponse>(
            `${environment.apiUrl}/api/blog-posts`,
            {
                params: {
                    'filters[slug][$eq]': slug,
                    'populate[textImages][populate]': 'image',
                    'populate[nextBlogPost][fields][0]': 'title',
                    'populate[nextBlogPost][fields][1]': 'slug',
                },
            },
        );
    }

    createContactEntry(data: ContactPayload): Observable<any> {
        return this.http.post(
            `${environment.apiUrl}/api/contact-requests`,
            data,
        );
    }

    createHofTourEntry(data: HoftourPayload): Observable<any> {
        return this.http.post(`${environment.apiUrl}/api/hof-tours`, data);
    }

    createProductRequest(data: ProductRequestPayload): Observable<any> {
        return this.http.post(
            `${environment.apiUrl}/api/product-requests`,
            data,
        );
    }

    createBookingEntry(data: BookingPayload): Observable<any> {
        return this.http.post(`${environment.apiUrl}/api/bookings`, data);
    }

    getApartmentConfig() {
        return this.http.get<ApartmentConfig>(
            `${environment.apiUrl}/api/config?pLevel=4`,
        );
    }

    getShopConfig() {
        return this.http.get<ShopConfig>(
            `${environment.apiUrl}/api/shop-config?pLevel=4`,
        );
    }

    getReservedBookings(
        startDate: Date,
        endDate: Date,
    ): Observable<ReservedBookings> {
        const isoStartDate = startDate.toISOString();
        const isoEndDate = endDate.toISOString();
        return this.http.get<ReservedBookings>(
            `${environment.apiUrl}/api/bookings?sort[0]=startDate:asc&filters[$or][0][startDate][$lte]=${isoStartDate}&filters[$or][0][endDate][$gt]=${isoStartDate}&filters[$or][1][startDate][$lt]=${isoEndDate}&filters[$or][1][endDate][$gte]=${isoEndDate}&filters[$or][2][startDate][$gte]=${isoStartDate}&filters[$or][2][endDate][$lte]=${isoEndDate}&fields[0]=startDate&fields[1]=endDate`,
        );
    }

    /**
     * Generic retry method that takes a factory function to create observables on each attempt.
     * @param factory Function that creates an Observable for the current attempt.
     * @param retries Maximum number of retry attempts.
     * @param delay Delay between retries in milliseconds.
     */
    retry<T>(
        factory: (attempt: number) => Observable<T>,
        retries: number,
        delay: number,
    ): Observable<T> {
        return new Observable<T>((observer) => {
            let attempts = 0;
            let subscription: any;

            const attempt = () => {
                subscription = factory(attempts).subscribe({
                    next: (value) => observer.next(value),
                    error: (error) => {
                        if (attempts < retries) {
                            attempts++;
                            setTimeout(attempt, delay * attempts * 3);
                        } else {
                            observer.error(error);
                        }
                    },
                    complete: () => observer.complete(),
                });
            };

            attempt();

            return () => {
                if (subscription) {
                    subscription.unsubscribe();
                }
            };
        });
    }

    /**
     * Handles retrying HTTP requests with Cache-Control header on retries.
     * @param requestFn Function that creates the HTTP request with given headers.
     * @param retries Number of retry attempts.
     * @param delay Delay between retries in milliseconds.
     */
    retryRequest<T>(
        requestFn: (headers: HttpHeaders) => Observable<T>,
        retries: number,
        delay: number,
    ): Observable<T> {
        const factory = (attempt: number) => {
            let headers = new HttpHeaders();
            if (attempt > 0) {
                headers = headers.set('Cache-Control', 'no-cache');
            }
            return requestFn(headers);
        };
        return this.retry(factory, retries, delay);
    }
}

export type OrderConfirmation = {
    orderReference: string;
    email: string;
    deliveryCountry: string;
    /** Null for pre-order orders without any dated item. */
    estimatedDeliveryDate: string | null;
    hasPreOrder?: boolean;
    /** Unique, sorted delivery dates of the dated items in the order. */
    deliveryDates?: string[];
};

export type ReservedBookings = {
    data: ReservedDate[];
};

export type ReservedDate = {
    startDate: string;
    endDate: string;
};

export type BookingPayload = {
    data: {
        startDate: string;
        endDate: string;
        adults: number;
        children: number;
        infants: number;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        message: string;
        street: string;
        houseNumber: string;
        postalCode: string;
        place: string;
    };
};

export type ContactPayload = {
    data: {
        name: string;
        email: string;
        message: string;
        contactTo: 'apartment' | 'hof';
    };
};

export type HoftourPayload = {
    data: {
        name: string;
        phoneNumber: string;
        desiredDate: string;
        desiredTime: string;
        message?: string;
    };
};

export type NewsletterPayload = {
    data: {
        email: string;
    };
};

export type ProductRequestPayload = {
    data: {
        email: string;
        product: {
            connect: string;
        };
    };
};

export type ApartmentConfig = {
    data: {
        bookingsPossible: boolean;
        maximumPeople: number;
        minimumStay: number;
        maximumStay?: number;
        checkInTime: string;
        checkOutTime: string;
        minimumPeople: number;
        minimumPeopleForBasePrices: number;
        possibleBookingDaysInAdvance: number;
        bookingsInPastPossible: boolean;
        seasons: Season[];
        blockedDates: Array<BlockedDate>;
    };
};

export type ShopConfig = {
    data: {
        weightText: BlockNode[];
        pieceText: BlockNode[];
        openingHours: BlockNode[];
        address: BlockNode[];
        ordersPossible: boolean;
        shippingEnabled: boolean;
        topBannerLink?: string | null;
        topBannerText?: string | null;
        preOrderText?: string | null;
    };
};

export type BlockedDate = DateValue | DateRange;

interface DateValue {
    __component: 'main.date-value';
    value: string;
}

interface DateRange {
    __component: 'main.date-range';
    start: string;
    end: string;
}

export type Season = {
    seasonName: string;
    seasonNameEnglish: string;
    subSeasons: SubSeason[];
    image: Image;
};

export type SubSeason = {
    subSeasonName: string;
    startDate: string;
    endDate: string;
    basePrice: number;
    cleaningCosts: number;
    adultPrice: number;
    childPrice: number;
};

export type GraphQLShopData = {
    data: {
        pages: Array<{
            slug: string;
            components: Array<{
                __component: string;
                maxProducts?: number;
                categories?: Category[];
            }>;
        }>;
    };
};

type CategoryResponse = {
    data: Category[];
};

type ProductResponse = {
    data: Product[];
};

export type SingleProductResponse = {
    data: Product;
};

type BlogPostResponse = {
    data: BlogPost[];
};
